<template>
  <div class="game-ui">
    <div class="question-section">
      <label class="question-label">Question</label>
      <div class="textarea-container">
        <b-form-textarea
          id="question-textarea"
          maxlength="4000"
          rows="3"
          v-model="questionText"
          @input="update()"
          required
          :readonly="!enableInputs"
        ></b-form-textarea>
      </div>
    </div>

    <div class="images-section">
      <div class="images-header">
        <label class="images-label">Images</label>
        <b-button 
          v-if="enableInputs" 
          @click="addImageBox" 
          class="add-image-btn"
          variant="outline-light"
          size="sm"
        >
          + Add Image
        </b-button>
      </div>
      <div class="images-container" v-if="images.length > 0">
        <div 
          v-for="(image, index) in images" 
          :key="index" 
          class="image-box-wrapper"
        >
          <div class="image-box" @click="triggerFileInput(index)">
            <input 
              v-if="enableInputs" 
              type="file" 
              :ref="`fileInput-${index}`" 
              @change="(e) => handleFileChange(e, index)" 
              style="display: none;" 
              accept="image/*"
            />
            <div v-if="image.url" class="image-container">
              <img :src="image.url" :alt="`Selected Image ${index + 1}`" class="uploaded-image" />
              <span v-if="enableInputs" class="edit-icon" @click.stop="triggerFileInput(index)">✎</span>
              <span 
                v-if="enableInputs" 
                class="delete-icon" 
                @click.stop="removeImage(index)"
              >
                ×
              </span>
            </div>
            <div v-else class="placeholder_file">Click to upload image</div>
          </div>
          <div 
            v-if="enableInputs && images.length > 1" 
            class="move-buttons"
          >
            <button @click="moveImageUp(index)" class="move-btn">↑</button>
            <button @click="moveImageDown(index)" class="move-btn">↓</button>
          </div>
        </div>
      </div>
      <div v-else class="no-images-message">
        No images added 
      </div>
    </div>

    <div class="options-section">
      <div class="options-header">
        <label class="options-label">Options</label>
        <b-button 
          v-if="enableInputs" 
          @click="addOption" 
          class="add-option-btn"
          variant="outline-light"
          size="sm"
        >
          + Add Option
        </b-button>
      </div>
      <div v-for="(option, index) in options" :key="index" class="option-item">
        <b-input-group class="option-group">
          <b-input-group-prepend v-if="!importFlagProp">
            <b-button 
              variant="danger" 
              @click="removeOption(index)"
              :disabled="options.length <= 2"
              class="remove-option-btn"
            >
              ×
            </b-button>
            <span class="option-label">{{ option.optionLabel }}</span>
          </b-input-group-prepend>
          <b-form-input
            v-model="option.text"
            @input="update()"
            :readonly="!enableInputs"
          ></b-form-input>
        </b-input-group>
      </div>
    </div>

    <div class="radio-answer-row">
      <div class="options-label-col">
        <label class="correct-answer-label">Correct Answer:</label>
      </div>
      <div class="options-radio-col">
        <b-form-radio-group
          v-model="answer"
          :options="options"
          class="radio-group"
          value-field="optionLabel"
          text-field="optionLabel"
          @input="update()"
          :disabled="!enableInputs"
        ></b-form-radio-group>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: Object,
    importFlagProp: Boolean,
    maxImages: {
      type: Number,
      default: 5
    },
    maxOptions: {
      type: Number,
      default: 6
    }
  },
  data() {
    return {
      images: this.value.files && this.value.files.length > 0 
        ? this.value.files.map(file => ({ file, url: file.url || '' }))
        : [],
      questionText: this.value.questionText || "",
      answer: this.value.answer || "A",
      id: this.value.id || "",
      importFlag: this.importFlagProp || false,
      options: this.value.options || [
        { text: "", optionLabel: "A" },
        { text: "", optionLabel: "B" }
      ],
      optionLetters: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'], // Support up to 8 options
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.questionText = newValue.questionText || "";
        this.answer = newValue.answer || 'A';
        this.id = this.value.id || "";
        
        if (newValue.files && newValue.files.length > 0) {
          this.images = newValue.files.map(file => ({ 
            file, 
            url: file?.url || this.getImageUrl(file) 
          }));
        } else {
          this.images = [];
        }

        this.options = newValue.options || [
          { text: "", optionLabel: "A" },
          { text: "", optionLabel: "B" }
        ];
      },
    },
  },
  computed: {
    enableInputs() {
      return !this.importFlagProp;
    }
  },
  methods: {
    getImageUrl(file) {
      if (file instanceof File) {
        return URL.createObjectURL(file);
      }
      return file?.url || "";
    },
    addImageBox() {
      if (this.images.length < this.maxImages) {
        this.images.push({ file: null, url: "" });
        this.update();
      }
    },
    removeImage(index) {
      this.images.splice(index, 1);
      this.update();
    },
    // Options management
    addOption() {
      if (this.options.length < this.maxOptions) {
        const nextLetter = this.optionLetters[this.options.length];
        this.options.push({ text: "", optionLabel: nextLetter });
        this.update();
      }
    },
    removeOption(index) {
      if (this.options.length > 2) {
        // If removing the currently selected answer, reset answer to empty
        if (this.answer === this.options[index].optionLabel) {
          this.answer = "";
        }
        this.options.splice(index, 1);
        
        // Reassign option labels to maintain sequence (A, B, C...)
        this.options.forEach((option, i) => {
          option.optionLabel = this.optionLetters[i];
        });
        
        this.update();
      }
    },
    moveImageUp(index) {
      if (index > 0) {
        const temp = this.images[index - 1];
        this.$set(this.images, index - 1, this.images[index]);
        this.$set(this.images, index, temp);
        this.update();
      }
    },
    moveImageDown(index) {
      if (index < this.images.length - 1) {
        const temp = this.images[index + 1];
        this.$set(this.images, index + 1, this.images[index]);
        this.$set(this.images, index, temp);
        this.update();
      }
    },
    triggerFileInput(index) {
      this.$refs[`fileInput-${index}`][0].click();
    },
    handleFileChange(event, index) {
      const file = event.target.files[0];
      if (file) {
        const url = URL.createObjectURL(file);
        this.$set(this.images, index, { file, url });
        this.update();
      }
    },
    update() {
      this.$emit("input-change", {
        id: this.id,
        questionText: this.questionText,
        files: this.images.map(img => {
          if(img.file || img.url)
          return img.file
        }),
        answer: this.answer,
        options: this.options
      });
    }
  }
};
</script>

<style scoped>
/* Main container */
.game-ui {
  font-family: 'Press Start 2P', cursive;
  color: #fff;
  background: linear-gradient(to right,var(--primary-color),#390879db, #390879ba ,#3908799e);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  padding: 20px;
}

/* Labels */
.question-label,
.answer-label,
.images-label {
  font-size: 1.2em;
  margin-bottom: 10px;
  display: block;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.images-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.add-image-btn {
  font-family: 'Press Start 2P', cursive;
  font-size: 0.7em;
  padding: 5px 10px;
}

.no-images-message {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9em;
  text-align: center;
  margin-bottom: 15px;
}

/* Textarea containers */
.textarea-container {
  margin-bottom: 20px;
}

/* Textareas */
#question-textarea,
#answer-textarea {
  width: 100%;
  border-radius: 10px;
  border: none;
  padding: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1em;
  resize: none;
  transition: transform 0.3s, box-shadow 0.3s;
}

#question-textarea:focus,
#answer-textarea:focus {
  outline: none;
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

/* Images section */
.images-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.image-box-wrapper {
  position: relative;
  margin-top: 1rem;
}

.image-box {
  width: 200px;
  height: 150px;
  border: 2px dashed rgba(255, 255, 255, 0.6);
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  padding: 5px;
  background: rgba(0, 0, 0, 0.2);
  transition: transform 0.3s, box-shadow 0.3s;
}

.image-box:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

/* Placeholder text */
.placeholder_file {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  padding: 10px;
}

/* Image container */
.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

/* Uploaded image */
.uploaded-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  object-fit: contain;
}

/* Edit icon */
.edit-icon {
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 3px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.3s;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-icon:hover {
  background-color: rgba(0, 0, 0, 0.9);
}

.delete-icon {
  position: absolute;
  top: 5px;
  left: 5px;
  background-color: rgba(255, 0, 0, 0.7);
  color: white !important;
  padding: 3px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.3s;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-primary);
}

.delete-icon:hover {
  background-color: rgba(255, 0, 0, 0.9);
}

.move-buttons {
  position: absolute;
  bottom: -25px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 5px;
}

.move-btn {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 3px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 10px;
}

.move-btn:hover {
  background: rgba(0, 0, 0, 0.9);
}


/* Options */
.option-item {
  margin-bottom: 10px;
}

.option-group {
  width: 100%;
  border-radius: 10px;
  border: none;
  padding: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1em;
  transition: transform 0.3s, box-shadow 0.3s;
}

.option-group b-form-input {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: none;
}

.option-group b-form-input:focus {
  outline: none;
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

/* Radio buttons */
.radio-answer-row {
  display: flex;
  align-items: center;
  margin-top: 20px;
}

.options-label-col {
  flex: 1;
}

.options-radio-col {
  flex: 2;
}

.radio-group {
  display: flex;
  justify-content: space-around;
}

.radio-group .custom-radio .custom-control-label::before {
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.6);
}

.radio-group .custom-radio .custom-control-input:checked~.custom-control-label::before {
  background-color: #2a5298;
  border-color: #1e3c72;
}


.options-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.add-option-btn {
  font-family: 'Press Start 2P', cursive;
  font-size: 0.7em;
  padding: 5px 10px;
}

.option-item {
  margin-bottom: 10px;
}

.option-group {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s, box-shadow 0.3s;
}

.option-group:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.remove-option-btn {
  border-radius: 0;
  font-weight: bold;
  padding: 0 10px;
  background-color: rgba(255, 0, 0, 0.7) !important;
  border: none;
}

.remove-option-btn:disabled {
  background-color: rgba(255, 0, 0, 0.3) !important;
  cursor: not-allowed;
}

.option-label {
  padding: 0 10px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.2);
  color: white;
  min-width: 30px;
  justify-content: center;
}

.option-group .form-control {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
}

.option-group .form-control:focus {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  box-shadow: none;
}

.input-group-prepend {
  margin-right: 0;
}

/* Radio buttons adjustments */
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

radio-group ::v-deep(.custom-control) {
  margin-right: 15px;
  margin-bottom: 5px;
}

.radio-group ::v-deep(.custom-control-label)::before {
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.6);
}

.radio-group ::v-deep(.custom-control-input:checked~.custom-control-label)::before {
  background-color: #2a5298;
  border-color: #1e3c72;
}

.radio-group ::v-deep(.custom-control-label) {
  cursor: pointer;
  padding-left: 5px;
}

@media (max-width: 768px) {
  .images-container {
    justify-content: center;
  }
  
  .image-box {
    width: 150px;
    height: 120px;
  }
}
</style>